<template>
  <tr
    v-chakra="
      (theme) => ({
        borderBottomWidth: theme.border.base,
        borderColor: 'gray.300',
        pb: { base: 'sm', lg: '0' },
        mb: { base: 'sm', lg: '0' },
        display: { base: 'grid', lg: 'table-row' },
        gap: 'sm',
        gridTemplateAreas: `
          'productNameLabel productName'
          'image availability'
          '. additionalNote'
          '. deliveryDelay'
          'productIdLabel productId'
          'priceLabel price'
          'quantityLabel quantityInput'
          'totalPriceLabel totalPrice'
        `,
      })
    "
  >
    <td v-chakra py="lg" pr="2xl" :display="{ base: 'contents', lg: 'table-cell' }">
      <c-flex align="center" :display="{ base: 'contents', lg: 'flex' }">
        <c-image
          :src="cartItem.product.imageUrl"
          :alt="cartItem.product.name"
          :max-width="100"
          :max-height="100"
          :object-fit="'contain'"
          :mr="{ lg: 'lg' }"
          grid-area="image"
        />
        <c-flex flex-direction="column" :display="{ base: 'contents', lg: 'flex' }">
          <c-text grid-area="productNameLabel" :display="{ base: 'block', lg: 'none' }">
            {{ $t("shoppingCart.heading.product") }}
          </c-text>
          <c-text
            :title="cartItem.product.name"
            cursor="pointer"
            font-weight="bold"
            @click="showProduct(cartItem.product)"
            grid-area="productName"
            :text-align="{ base: 'right', lg: 'left' }"
            tabindex="0"
          >
            {{ cartItem.product.name }}
          </c-text>

          <c-text grid-area="availability" :text-align="{ base: 'right', lg: 'left' }">
            <availability-info
              :availability="cartItem.availability"
              :availability-date="cartItem.availabilityDate"
            />
          </c-text>

          <c-text
            v-if="cartItem.product.additional_note"
            class="additional_note"
            grid-area="additionalNote"
            :text-align="{ base: 'right', lg: 'left' }"
            data-cy="shopping-cart-item-additional-note"
          >
            {{ cartItem.product.additional_note }}
          </c-text>
          <c-text
            v-if="cartItem.product.deliveryDelayInDays > 1"
            :title="
              $t('shoppingCart.deliveryDelay', { days: cartItem.product.deliveryDelayInDays })
            "
            class="product-note"
            grid-area="deliveryDelay"
            :text-align="{ base: 'right', lg: 'left' }"
            data-cy="shopping-cart-item-delivery-delay"
          >
            {{ $t("shoppingCart.deliveryDelay", { days: cartItem.product.deliveryDelayInDays }) }}
          </c-text>
        </c-flex>
      </c-flex>
    </td>

    <td v-chakra py="lg" pr="2xl" :display="{ base: 'contents', lg: 'table-cell' }">
      <c-text grid-area="productIdLabel" :display="{ base: 'block', lg: 'none' }">
        {{ $t("shoppingCart.heading.productId") }}
      </c-text>
      <c-text as="span" grid-area="productId" :text-align="{ base: 'right', lg: 'left' }">
        <span v-html="addBreakingHintsToSku(cartItem.product.id)" />
      </c-text>
    </td>

    <td v-chakra py="lg" pr="2xl" :display="{ base: 'contents', lg: 'table-cell' }">
      <label class="sr-only" :for="`quantity_${cartItem.product.id}`">
        {{ $t("shoppingCart.heading.quantity") }}
      </label>

      <c-text
        grid-area="quantityLabel"
        :display="{ base: 'flex', lg: 'none' }"
        align-items="center"
      >
        {{ $t("shoppingCart.heading.quantity") }}
      </c-text>

      <c-box
        position="relative"
        grid-area="quantityInput"
        :display="{ base: 'flex', lg: 'block' }"
        align-items="center"
      >
        <c-input
          :id="`quantity_${cartItem.product.id}`"
          v-model.number="cartItem.quantity"
          min="1"
          max="2000"
          type="number"
          data-cy="shopping-cart-item-quantity"
          :min-width="{ lg: '5rem' }"
          @focus="$event.target.select()"
          @blur="onQuantityBlur && onQuantityBlur(cartItem)"
          @input="onQuantityChange && onQuantityChange(cartItem)"
          :border-color="isItemOverProductSpecificLimit(cartItem) ? 'brand.red' : 'brand.grayDark'"
        />
        <c-text
          v-if="isItemOverProductSpecificLimit(cartItem)"
          color="white"
          position="absolute"
          top="calc(100% + 0.75rem)"
          left="50%"
          transform="translateX(-50%)"
          text-align="center"
          background="gray.700"
          border-radius="input"
          padding="0.75rem"
          :width="{ base: '100%', lg: '300%' }"
          :_before="{
            content: '\'\'',
            position: 'absolute',
            top: '-0.1rem',
            left: '50%',
            width: '1rem',
            height: '1rem',
            background: 'gray.700',
            transform: 'rotate(45deg) translateX(-50%)',
          }"
          data-cy="shopping-cart-item-quantity-error"
        >
          {{ $t("shoppingCart.maxOrderQuantity.title") + " " + cartItem.product.maxOrderQuantity }}
        </c-text>
        <c-text as="span" :display="{ lg: 'none' }" ml="xs">
          <a href="#" class="deleteLink" @click="removeFromCart(cartItem)">
            <c-icon name="trashcan" size="2rem" color="brand.purple" />
          </a>
        </c-text>
      </c-box>
    </td>
    <td v-chakra py="lg" pr="2xl" :display="{ base: 'contents', lg: 'table-cell' }">
      <c-text grid-area="priceLabel" :display="{ base: 'block', lg: 'none' }">
        {{ $t("shoppingCart.heading.unitPrice") }}
      </c-text>
      <Price
        v-chakra
        grid-area="price"
        white-space="nowrap"
        :text-align="{ base: 'right', lg: 'left' }"
        :price="cartItem.product.price.price"
        :campaign-price="campaignPrice(cartItem, discounts)"
        :currency="cartItem.product.price.currencyCode"
      />
    </td>
    <td
      v-chakra
      py="lg"
      :pr="{ base: 'md', xl: '2xl' }"
      :display="{ base: 'contents', lg: 'table-cell' }"
      :width="{ base: '5rem', xl: 'auto' }"
      :min-width="{ md: '5rem', xl: '9rem' }"
      :max-width="{ lg: '11rem' }"
      overflow="hidden"
    >
      <c-text grid-area="totalPriceLabel" :display="{ base: 'inline-grid', lg: 'none' }">
        {{ $t("shoppingCart.heading.totalPrice") }}
      </c-text>
      <Price
        v-chakra
        :white-space="{ base: 'pre-line', xl: 'nowrap' }"
        :word-break="{ base: 'break-all', xl: 'normal' }"
        grid-area="totalPrice"
        :text-align="{ base: 'right', lg: 'left' }"
        :price="cartItem.rowTotalSum"
        :currency="cartItem.product.price.currencyCode"
      />
    </td>
    <td v-if="removeFromCart" v-chakra py="lg" :display="{ base: 'none', lg: 'table-cell' }">
      <c-text as="span" :text-align="{ base: 'right', lg: 'left' }">
        <a href="#" class="deleteLink" @click="removeFromCart(cartItem)">
          <c-icon name="trashcan" size="2rem" color="brand.purple" />
        </a>
      </c-text>
    </td>
  </tr>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Price from "@/design-system/elements/Price.vue";
import { IOrderRow } from "@iloq/shared/IOrderRow";
import { CBox, CInput, CIcon, CFlex, CImage, CText } from "@chakra-ui/vue";
import AvailabilityInfo from "../../components/ordering/AvailabilityInfo.vue";
import { mapState } from "vuex";
import { IRootState } from "@iloq/shared";
import { orderMixin } from "./OrderMixins";

export default defineComponent({
  name: "ShoppingCartItem",
  components: {
    Price,
    CBox,
    CInput,
    CIcon,
    CFlex,
    CImage,
    CText,
    AvailabilityInfo,
  },
  mixins: [orderMixin],
  computed: {
    ...mapState({
      discounts: (state: unknown) => (state as IRootState).shoppingCart?.discounts,
    }),
  },
  props: {
    showProduct: Function,
    removeFromCart: Function,
    cartItem: { type: Object as PropType<any>, required: true },
    hideImage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showProductSpecificMaxOverLimitError: false,
    };
  },
  methods: {
    addBreakingHintsToSku(input: string): string {
      return input.replace(/\./g, ".<wbr>");
    },
    isItemOverProductSpecificLimit(cartItem: IOrderRow) {
      return cartItem.product.maxOrderQuantity
        ? cartItem.quantity > cartItem.product.maxOrderQuantity
        : false;
    },
    onQuantityChange(cartItem: IOrderRow): void {
      // Prevent user to make orders with invalid amount
      const intQuantity = parseInt(cartItem.quantity as any, 10);
      if (intQuantity <= 0) {
        cartItem.quantity = 1;
      } else if (isNaN(intQuantity)) {
        // Allow emptying the field while editing but not when blurring (see onQuantityBlur())
      } else {
        cartItem.quantity = intQuantity;
      }
      const isProductSpecificQuantityOverLimit = this.isItemOverProductSpecificLimit(cartItem);
      if (isProductSpecificQuantityOverLimit) {
        this.showProductSpecificMaxOverLimitError = true;
      } else {
        this.showProductSpecificMaxOverLimitError = false;
        this.$store.dispatch("shoppingCart/calculateSums");
      }
    },
    onQuantityBlur(cartItem: IOrderRow): void {
      // Prevent user from leaving quantity empty
      if (isNaN(parseInt(cartItem.quantity as any, 10))) {
        cartItem.quantity = 1;
        this.$store.dispatch("shoppingCart/calculateSums");
      }
    },
  },
});
</script>
